// A message shown as a RESULT — search hits, saved messages, mentions, the pinned board. One look for
// all of them: who, where, when, then the words; the whole row opens the message in place.
import type { ReactNode } from 'react';
import { Icon, type IconName } from '../../design/icons';
import { Avatar } from '../Avatar';

export function PostResultRow({
  userId,
  authorName,
  authorVersion = 0,
  place,
  placeIsPrivate,
  placeIcon,
  time,
  snippet,
  onOpen,
  trailing,
  badge,
}: {
  userId: string;
  authorName: string;
  authorVersion?: number;
  /** Channel / conversation name. */
  place: string;
  placeIsPrivate?: boolean;
  /** Overrides the place glyph (a DM is a person, a group several — not a `#`). */
  placeIcon?: IconName;
  time: string;
  /** Plain text or highlighted nodes (mark.hit). */
  snippet: ReactNode;
  onOpen(): void;
  trailing?: ReactNode;
  badge?: ReactNode;
}) {
  return (
    // The row opens on a click anywhere (mouse); the keyboard/AT target is ONE real button holding
    // who/where/when/words, so a trailing control (unsave, …) is never nested inside it (P255).
    <div className="post-result" onClick={onOpen}>
      <button type="button" className="post-result-main" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
        <Avatar userId={userId} name={authorName} size={32} version={authorVersion} />
        <span className="post-result-body">
          <span className="meta-line">
            <strong>{authorName}</strong>
            <span className="place">
              <Icon name={placeIcon ?? (placeIsPrivate ? 'lock' : 'hash')} size={12} />
              {place}
            </span>
            <span>{time}</span>
            {badge}
          </span>
          <span className="snippet">{snippet}</span>
        </span>
      </button>
      {trailing ? <div onClick={(e) => e.stopPropagation()}>{trailing}</div> : null}
    </div>
  );
}

/** Highlights [start,end) ranges (UTF-16) of `text` with <mark class="hit">. */
export function Highlighted({ text, ranges }: { text: string; ranges: Array<{ start: number; end: number }> }) {
  if (!ranges.length) return <>{text}</>;
  const out: ReactNode[] = [];
  let cursor = 0;
  ranges
    .slice()
    .sort((a, b) => a.start - b.start)
    .forEach((r, i) => {
      if (r.start < cursor) return;
      if (r.start > cursor) out.push(text.slice(cursor, r.start));
      out.push(
        <mark key={i} className="hit">
          {text.slice(r.start, r.end)}
        </mark>,
      );
      cursor = r.end;
    });
  if (cursor < text.length) out.push(text.slice(cursor));
  return <>{out}</>;
}
